/**
 * The ONE kg text parser for every shipping-weight input: Default shipping
 * weights (`DefaultWeightsSection`, Production Settings) and the 3PL's
 * per-location shipping weight (`ShippingWeightOverrideModal`, Products). Same
 * unit (kg), same bounds as the backend schema and table CHECK
 * (`DEFAULT_WEIGHT_MIN_KG`..`DEFAULT_WEIGHT_MAX_KG`), same 3-decimal guard, so
 * both surfaces accept and refuse exactly the same text. It lives in `lib/`
 * because two features use it; a plain `.ts` module also keeps
 * `react-refresh/only-export-components` happy.
 *
 * Inputs feeding this are RAW TEXT (`type="text" inputMode="decimal"`), never
 * `type="number"`: a number input reports '' for a half-typed `0.2e`, which
 * would read as "cleared" (DESIGN.md §12.1).
 */
import { DEFAULT_WEIGHT_MAX_KG, DEFAULT_WEIGHT_MIN_KG } from '@threadheads/shared';

export type KgParse =
  | { kind: 'blank' }
  | { kind: 'invalid' }
  | { kind: 'tooPrecise' }
  | { kind: 'ok'; kg: number };

/** Plain decimal only: `0.2`, `.2`, `1.` (no sign, exponent or separators). */
const KG_TEXT_RE = /^(?:\d+(?:\.\d*)?|\.\d+)$/;

/**
 * Chinese input, folded to ASCII before anything reads the text: a Pinyin IME
 * types `。` (U+3002) for the period, and full-width mode types `０．２５`.
 * A targeted map, never NFKC: full-width digits `０`-`９` (U+FF10-U+FF19)
 * become `0`-`9`, and the three periods a Chinese keyboard can type (`。`
 * U+3002, full-width `．` U+FF0E, half-width `｡` U+FF61) become `.`. Then
 * `trim()`, which already strips the ideographic space (U+3000). NFKC would
 * also fold about 180 look-alikes (superscripts, circled digits, maths
 * digits, the one-dot leader), so `0.5²` would save as 0.52 kg and `①` as
 * 1 kg; everything outside this map stays as typed and is refused.
 *
 * Exported under a general name because nothing in it is about kg: every
 * numeric text field a Chinese keyboard types into (kg here, client prices in
 * `features/client-prices`) folds its input through this one map, so they all
 * accept exactly the same characters.
 */
export const normalizeNumericText = (text: string): string =>
  text
    .replace(/[\uFF10-\uFF19]/g, (d) => String.fromCharCode(d.charCodeAt(0) - 0xfee0))
    .replace(/[\u3002\uFF0E\uFF61]/g, '.')
    .trim();

export function parseKg(text: string): KgParse {
  const s = normalizeNumericText(text);
  if (s === '') return { kind: 'blank' };
  if (!KG_TEXT_RE.test(s)) return { kind: 'invalid' };
  const kg = Number(s);
  if (!Number.isFinite(kg) || kg < DEFAULT_WEIGHT_MIN_KG || kg > DEFAULT_WEIGHT_MAX_KG) {
    return { kind: 'invalid' };
  }
  // The backend stores 3 dp (grams). Refuse more instead of letting the save
  // silently round what the operator typed.
  const decimals = (s.split('.')[1] ?? '').replace(/0+$/, '').length;
  if (decimals > 3) return { kind: 'tooPrecise' };
  return { kind: 'ok', kg };
}

/**
 * True while `text` may still become a valid weight by typing more characters
 * (`''`, `'0'`, `'0.'`, `'.'`, `'0.0'`, `'0.00'`): a live field should not
 * flag those as errors yet. False once no continuation can be valid (`'31'`,
 * `'abc'`, `'0.1234'`, `'-'`). Checking the text itself and the text plus a
 * `1` is enough: when any continuation is valid, that one is too (a value
 * that is too large or too precise only gets worse with more characters).
 */
export function kgTextCanBecomeValid(text: string): boolean {
  const s = normalizeNumericText(text);
  return [s, `${s}1`].some((candidate) => parseKg(candidate).kind === 'ok');
}

/**
 * The ONE error rule for a live kg field: the error kind to show, or null.
 * Text that can still become valid stays quiet until the field is
 * `committed` (since its last edit it lost focus or took Enter); text no
 * continuation can fix shows at once. Blank is "not entered yet", never an
 * error.
 */
export function kgFieldError(text: string, committed: boolean): 'invalid' | 'tooPrecise' | null {
  const parsed = parseKg(text);
  if (parsed.kind !== 'invalid' && parsed.kind !== 'tooPrecise') return null;
  return committed || !kgTextCanBecomeValid(text) ? parsed.kind : null;
}

/** Display a kg value at the stored precision (3 dp, trailing zeros dropped). */
const kgFormatter = new Intl.NumberFormat(undefined, { maximumFractionDigits: 3 });
export const formatKg = (kg: number): string => kgFormatter.format(kg);
